import {
    AppSku,
    CommunityItemContract,
    CommunityItemCreateContract,
    EntityType,
} from '@polaris-customerweb/sharedcomponents.core';
import {
    GeoBounds,
    GeoCoordinate,
} from '@polaris-customerweb/sharedcomponents.shared';
import { UserContentItem } from '@polaris-customerweb/sharedcomponents.user-content';
import { cloneDeep } from 'lodash-es';
import { CommunityFeedback } from './community-feedback';
import { Content } from './content';

export class CommunityItem implements UserContentItem {
    readonly type: EntityType = 'CommunityItem';

    private rawCommunityItem: CommunityItemContract;
    private _content: Content;
    private _creationDate: Date;
    private _modificationDate: Date;

    constructor(
        rawCommunityItem: CommunityItemContract = {
            content: {},
        } as unknown as CommunityItemContract
    ) {
        this.rawCommunityItem = cloneDeep(rawCommunityItem);
    }

    get appSku(): AppSku {
        return this.rawCommunityItem.appSku || 'ORV';
    }

    get bounds(): GeoBounds {
        if (!this.rawCommunityItem.boundingBox) {
            return new GeoBounds();
        }

        return new GeoBounds(...this.rawCommunityItem.boundingBox);
    }

    get creationDate(): Date {
        if (!this._creationDate && this.rawCommunityItem.creationDate) {
            this._creationDate = new Date(this.rawCommunityItem.creationDate);
        }

        return this._creationDate || undefined;
    }

    get description(): string {
        return this.rawCommunityItem.description || '';
    }
    set description(value: string) {
        this.rawCommunityItem.description = value;
    }

    get favoriteId(): string {
        return this.rawCommunityItem.favoritesCollectionId || '';
    }
    set favoriteId(value: string) {
        this.rawCommunityItem.favoritesCollectionId = value;
    }

    get geoCoordinate(): GeoCoordinate {
        return this.content?.contentItem?.geoCoordinate;
    }

    get id(): string {
        return this.rawCommunityItem.id || '';
    }
    set id(id: string) {
        this.rawCommunityItem.id = id;
    }

    get modificationDate(): Date {
        if (!this._modificationDate && this.rawCommunityItem.modificationDate) {
            this._modificationDate = new Date(
                this.rawCommunityItem.modificationDate
            );
        }

        return this._modificationDate;
    }
    set modificationDate(date: Date) {
        this._modificationDate = date;
        this.rawCommunityItem.modificationDate = date.toISOString();
    }

    get name(): string {
        return this.rawCommunityItem.name || '';
    }
    set name(value: string) {
        this.rawCommunityItem.name = value;
    }

    get riderId(): string {
        return this.rawCommunityItem.riderId || '';
    }

    get rideType(): string {
        return this.rawCommunityItem.rideType || '';
    }

    get sharedByName(): string {
        return this.rawCommunityItem.sharedByName || '';
    }

    get content(): Content {
        if (!this._content) {
            this._content = new Content(this.rawCommunityItem.content);
        }

        return this._content;
    }
    set content(content: Content) {
        this._content = content;
    }

    get communityFeedback(): CommunityFeedback {
        return new CommunityFeedback(this.rawCommunityItem.communityContent);
    }
    set communityFeedback(communityFeedback: CommunityFeedback) {
        this.rawCommunityItem.communityContent = communityFeedback.toContract();
    }

    get linkedType(): EntityType {
        return this.content.linkedType;
    }

    static fromUserContentItem(item: UserContentItem): CommunityItem {
        const communityItemContract: CommunityItemContract = {
            boundingBox: item.bounds?.toGeoJsonBoundingBox(),
            communityContent: {},
            content: Content.fromUserContentItem(item).toContract(),
            description: item.description,
            latitude: item.geoCoordinate?.latitude,
            longitude: item.geoCoordinate?.longitude,
            name: item.name,
            riderId: item.riderId,
            vehicleTypes: [],
        };

        return new CommunityItem(communityItemContract);
    }

    clone(): CommunityItem {
        const communityItem = new CommunityItem(this.toContract());
        communityItem.content = this.content;
        return communityItem;
    }

    toCommunityItemCreateDto(): CommunityItemCreateContract {
        return {
            description: this.description,
            id: this.id,
            linkedItem: {
                entityType: this.linkedType,
                id: this.content?.contentItem?.id,
            },
            name: this.name,
        };
    }

    toContract(): CommunityItemContract {
        return cloneDeep(this.rawCommunityItem);
    }

    // This is called implicitly when Angular's $http service serializes the data property.
    toJSON(): CommunityItemContract {
        return this.toContract();
    }
}
